import { defineStore } from 'pinia'
import { streamStore } from './stream'
import { AxiosError } from 'axios'
import { uuid, s3Client } from '../helpers'
import { ListObjectsV2Command, DeleteObjectCommand } from '@aws-sdk/client-s3'
import { useUserStore } from './userStore'
import type { JobTelemetryItem } from '@/types/jobTelemetry'
import { mqttStore } from './mqtt'
import type { Telemetry, HighFrequencyTelemetry, PortalStatus } from '@/types/dashboard.types'
import { useApi } from '@/config/apiFetcher'
import { type DockState } from '@/types/dock.types'
import { findDevice, groupDevices } from '@/utils/idi-utils'
import log from '@/utils/useLogger'
import type { Stream } from '@/types/dashboard.types'
import type { AppMode } from '@/constants/appData'
import type { Device, DeviceGroup } from '@/types/devices.types'
// Define device interface
interface DeviceInfo {
  deviceID: string
  deviceGroupID: string
  deviceType: string
  [key: string]: any // Consider replacing with more specific typing
}

interface Mission {
  index: number
  name: string
  type: string
  Key?: string
  waypoints?: any
  new?: boolean
}

import {
  HEALTH_MESSAGE_TTL_MS,
  MAX_HEALTH_MESSAGES_PER_MODULE,
  countHealthMessages,
  pruneStaleHealthMessages as pruneHealthMessages,
  type DeviceHealthMessage,
  type DeviceHealthMessages,
  type HealthLogState,
} from '@/composables/Device/deviceHealth'

interface NotificationType {
  notification: string
  type: string
  id: string
}

interface ConfirmAction {
  showModal: boolean
  action: string
}

interface AppState {
  appMode: AppMode
  portalStatus: PortalStatus
  telemetry: Telemetry | null
  jobTelemetry: JobTelemetryItem[] | null
  droneAmsl: number
  dockAmsl: number
  cloudData: any
  cloudDataSource: string | null
  dockState: DockState | null
  dockStatus: any
  cloudAddress: string
  gtlDetails: {
    height: number
    speed: number
  }
  isPlottingEntities: boolean
  countdown: number
  useDashboardGimbalControls: boolean
  goToMusterPointActive: boolean
  currentFlightHeight: number
  currentHeightSpeed: number
  isSafeLocationSet: boolean
  isDockLocationSet: boolean
  goToAircrafts: boolean
  airCraftIndex: number
  droneIncursions: any
  hasCriticalIncursions: boolean
  hasWarningIncursions: boolean
  lastLoginTime: any
  aeroToken: any
  rcState: any
  goToLocationActive: boolean
  gtlPoints: any[]
  recenter: boolean
  automation_state: any
  notifications: NotificationType[]
  performMapAction: boolean
  clearMap: boolean
  showHeatMap: boolean
  activeImportedMission: string | null
  dockConfigurations: any
  failSafeConfigurations: any
  droneConfigurations: any
  slinkTechConfigurations: any
  streamConfigurations: any
  device: { deviceID: string; organisationID: string } | null
  cameraConfig: any
  isVirtualStickEnable: boolean
  virtualStickInterval: any
  recordingStatus: boolean
  importedMissions: Mission[]
  isIntegrationActive: string | null
  deviceGroupIDs: { deviceGroupID: string; deviceGroupName?: string }[] // groups assigned to user
  selectedDevice: string | null
  weatherData: any
  deviceHealthMessages: DeviceHealthMessages
  gtl: boolean
  confirmAction: ConfirmAction
  volantLoading: boolean
  flyStyleURL: string
  autoApprove: boolean
  rasterOpacityValue: number
  pathGenerated: boolean
  toolTipTimer: ReturnType<typeof setTimeout> | null
  toolTipContent: any
  tooltipPosition: Record<string, any>
  rollMin: number
  rollMax: number
  geoidHeight: number
  camStream: Stream | null
  organizationDevices: Device[]
}

// Single source of truth for default state
const getDefaultState = (): AppState => ({
  appMode: 'FLY_MODE',
  camStream: null,
  portalStatus: 'idle',
  isPlottingEntities: false,
  useDashboardGimbalControls: true,
  telemetry: null,
  jobTelemetry: null,
  droneAmsl: 0,
  geoidHeight: 0,
  dockAmsl: 0,
  cloudData: null,
  cloudDataSource: null,
  dockState: null,
  cloudAddress: '',
  dockStatus: null,
  gtlDetails: {
    height: 0,
    speed: 0,
  },
  goToMusterPointActive: false,
  countdown: 0,
  isSafeLocationSet: false,
  currentFlightHeight: 0,
  currentHeightSpeed: 0,
  isDockLocationSet: false,
  goToAircrafts: false,
  airCraftIndex: 0,
  droneIncursions: null,
  hasCriticalIncursions: false,
  hasWarningIncursions: false,
  lastLoginTime: null,
  rcState: null,
  aeroToken: null,
  goToLocationActive: false,
  gtlPoints: [],
  recenter: false,
  automation_state: null,
  notifications: [],
  performMapAction: false,
  clearMap: false,
  showHeatMap: false,
  activeImportedMission: null,
  dockConfigurations: null,
  failSafeConfigurations: null,
  droneConfigurations: null,
  slinkTechConfigurations: null,
  streamConfigurations: null,
  device: null,
  cameraConfig: null,
  isVirtualStickEnable: false,
  virtualStickInterval: null,
  recordingStatus: false,
  importedMissions: [],
  isIntegrationActive: null,
  deviceGroupIDs: [],
  selectedDevice: null,
  weatherData: null,
  deviceHealthMessages: {},
  gtl: false,
  confirmAction: {
    showModal: false,
    action: '',
  },
  flyStyleURL: 'mapbox://styles/mapbox/satellite-streets-v11',
  autoApprove: false,
  volantLoading: false,
  rasterOpacityValue: 0,
  pathGenerated: false,
  toolTipTimer: null,
  toolTipContent: null,
  tooltipPosition: {},
  rollMin: -20,
  rollMax: 20,
  organizationDevices: [],
})

// Telemetry leaves owned by the high-frequency stream. The low-frequency merge
// must skip these so its slower, less accurate values do not stomp the fresh
// high-freq readings between ticks. Mirror this list against
// mergeHighFrequencyTelemetry.
const HIGH_FREQ_OWNED_PATHS: readonly string[] = [
  'gimbalCamera.gimbal.pitch',
  'gimbalCamera.gimbal.yaw',
  'gimbalCamera.gimbal.roll',
  'aircraftState.height',
  'aircraftState.heading',
  'aircraftState.speedX',
  'aircraftState.speedY',
  'aircraftState.speed',
  'aircraftState.distanceToHome',
  'locationNavigation.latitude',
  'locationNavigation.longitude',
  'locationNavigation.ultrasonicHeight',
  // environment.windSpeed / windDirection are deliberately NOT owned here — see
  // mergeHighFrequencyTelemetry.
]
const HIGH_FREQ_STALE_MS = 2500
let lastHighFreqAt = 0

export const appStore = defineStore('app', {
  state: getDefaultState,

  getters: {
    selectedRC(): string {
      return findDevice(this.selectedDevice, 'rc', false)
    },
    selectedRCObj(): DeviceInfo | null {
      return findDevice(this.selectedDevice, 'rc', true) as DeviceInfo | null
    },
    selectedPortal(): string {
      return findDevice(this.selectedDevice, 'slinktech', false)
    },
    selectedDock(): string {
      return findDevice(this.selectedDevice, 'dock', false)
    },
    selectedDockObj(): DeviceInfo | null {
      return findDevice(this.selectedDevice, 'dock', true) as DeviceInfo | null
    },
    isDjiDock3(): boolean {
      return this.selectedDockObj?.deviceType === 'DJI_DOCK_3'
    },
    groupedDevices(): DeviceGroup[] {
      if (!this.organizationDevices.length) return []
      return groupDevices(this.organizationDevices)
    },
    panTiltLimits(): {
      tiltMin: number
      tiltMax: number
      panMin: number
      panMax: number
      rateChangePanTilt: number
    } {
      try {
        const currentZoom = this.telemetry?.gimbalCamera?.camera?.zoomRatios
          ? this.telemetry.gimbalCamera.camera.zoomRatios
          : 1
        const zoomRange = this.cameraConfig?.CameraZoomRatiosRange
        const maxZoom =
          Array.isArray(zoomRange) && zoomRange.length > 0 ? Math.max(...zoomRange) : 54
        let zoomPercentage = Number(currentZoom) / maxZoom

        if (isNaN(zoomPercentage)) {
          zoomPercentage = 0
        }

        let scaleFactor = 1 - zoomPercentage * 0.9
        const a = 1.4
        const b = 0.4
        const _rateChangePanTilt = Math.max(
          0.1,
          0.8 - a * zoomPercentage + b * zoomPercentage * zoomPercentage * zoomPercentage,
        )

        let baseTiltMin = -20
        let baseTiltMax = 20
        let basePanMin = -20
        let basePanMax = 20

        if (this.telemetry?.gimbalCamera?.camera?.videoStreamSourceType !== 'ZOOM_CAMERA') {
          baseTiltMin = -20
          baseTiltMax = 20
          basePanMin = -20
          basePanMax = 20
          scaleFactor = 1
        } else if (this.telemetry?.gimbalCamera?.camera?.videoStreamSourceType === 'ZOOM_CAMERA') {
          baseTiltMin = -6
          baseTiltMax = 6
          basePanMin = -6
          basePanMax = 6
        }

        return {
          tiltMin: Math.max(-20, Math.min(-1, Math.round(baseTiltMin * scaleFactor))),
          tiltMax: Math.min(20, Math.max(1, Math.round(baseTiltMax * scaleFactor))),
          panMin: Math.max(-20, Math.min(-1, Math.round(basePanMin * scaleFactor))),
          panMax: Math.min(20, Math.max(1, Math.round(basePanMax * scaleFactor))),
          rateChangePanTilt: _rateChangePanTilt,
        }
      } catch (err) {
        log.error('Error in panTiltLimits getter:', err)
        return {
          tiltMin: -20,
          tiltMax: 20,
          panMin: -20,
          panMax: 20,
          rateChangePanTilt: 1,
        }
      }
    },
  },

  actions: {
    setProperty(prop: keyof AppState, payload: any): void {
      ;(this[prop] as any) = payload
    },

    resetGTLPoints(): void {
      this.gtlPoints = []
    },

    addNotifications(payload: NotificationType): void {
      // Validate payload
      if (!payload || !payload.id || !payload.notification) return

      // Check for duplicates in notifications array
      if (this.notifications.some((n) => n.notification === payload.notification)) return

      // Add notification
      if (this.notifications.length < 3) {
        this.notifications.unshift(payload)
      } else {
        this.notifications.pop()
        this.notifications.unshift(payload)
      }
      // Schedule removal
      setTimeout(() => {
        this.removeNotification(payload.id)
      }, 15000)
    },

    clearNotification(index: number): void {
      this.notifications.splice(index, 1)
    },

    removeNotification(payloadId: string): void {
      this.notifications = this.notifications.filter(
        (notification) => notification.id !== payloadId,
      )
    },

    async generateVolantPath(payload: any): Promise<void> {
      this.volantLoading = true

      if (this.showHeatMap) {
        this.showHeatMap = false
        setTimeout(() => {
          this.showHeatMap = true
        }, 1000)
      } else {
        this.showHeatMap = true
      }

      const api = useApi()
      const url = `https://gzakidxm74.execute-api.eu-west-2.amazonaws.com/Prod/mission-planning/get-volant-path`
      try {
        const response = await api({
          method: 'POST',
          url,
          data: JSON.stringify(payload),
          headers: {
            'Content-type': 'application/json',
          },
        })

        if (response.status >= 200 && response.status < 300) {
          this.importedMissions = this.importedMissions.filter((mission) => !mission.new)
          this.volantLoading = false
          // load missions
          await this.loadMissions()
          this.importedMissions.push({
            index: this.importedMissions.length,
            name: `Volant-${uuid(4)}`,
            type: 'volant-mission',
            waypoints: response.data.waypoint_list,
            new: true,
          })
          this.pathGenerated = true

          setTimeout(() => {
            this.pathGenerated = false
          }, 3000)
        } else {
          this.addNotifications({
            notification: response.data?.error || 'Unknown error',
            type: 'error',
            id: uuid(10),
          })
          this.volantLoading = false
        }
      } catch (error) {
        const err = error as AxiosError
        if (
          err.response &&
          err.response.data &&
          typeof err.response.data === 'object' &&
          'error' in err.response.data
        ) {
          this.addNotifications({
            notification: (err.response.data as any).error || 'Unknown error',
            type: 'error',
            id: uuid(10),
          })
        } else {
          this.addNotifications({
            notification: 'Unknown error occurred',
            type: 'error',
            id: uuid(10),
          })
        }
        this.volantLoading = false
      }
    },

    mergeMissions(payload: Partial<Mission>, index: number): void {
      if (this.importedMissions[index]) {
        this.importedMissions[index] = { ...this.importedMissions[index], ...payload }
      }
    },

    async loadMissions(): Promise<void> {
      const { userDetails } = useUserStore()
      const bucketName = 'dock-s-missions'
      const orgId = userDetails?.['custom:attribute1'] || ''
      const userId = userDetails?.['sub'] || ''
      const folderName = `${orgId}/${userId}`

      const listObjectsCommand = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: folderName,
      })

      try {
        const data = await s3Client.send(listObjectsCommand)
        const missionsData =
          data.Contents?.filter((file) => file.Key?.endsWith('.kmz')).map((file, index) => ({
            name: file.Key?.split('/').pop() || '',
            Key: file.Key || '',
            type: 'mission',
            index: index,
          })) || []

        this.importedMissions = missionsData
        if (this.importedMissions.length > 0) {
          this.activeImportedMission = this.importedMissions[0].name
        } else {
          this.activeImportedMission = null
        }
      } catch (err) {
        log.error('Error loading missions:', err)
        this.addNotifications({
          notification: 'Failed to load missions',
          type: 'error',
          id: uuid(10),
        })
      }
    },

    async deleteMission(fileName: string): Promise<void> {
      const bucketName = 'dock-s-missions'
      const params = {
        Bucket: bucketName,
        Key: fileName,
      }
      try {
        const deleteCommand = new DeleteObjectCommand(params)
        await s3Client.send(deleteCommand)
        await this.loadMissions()
      } catch (err) {
        log.error('Error deleting mission:', err)
        this.addNotifications({
          notification: 'Failed to delete mission',
          type: 'error',
          id: uuid(10),
        })
      }
    },

    resetConfigurations(): void {
      this.dockConfigurations = null
      this.droneConfigurations = null
      this.slinkTechConfigurations = null
      this.cameraConfig = null
      this.streamConfigurations = null
      this.failSafeConfigurations = null
      // Reset dockAmsl so the next group's `addDockLocationExperimental` fetches
      // a fresh terrain height instead of reusing the prior group's value.
      this.dockAmsl = 0
    },
    /**
     * Clear every device-scoped slice of app state on a group switch so the
     * next device can't display the previous device's telemetry, incursions,
     * flight UI flags, or running timers.
     *
     * Complements `resetConfigurations()` (which only handles device configs +
     * `dockAmsl`). Session/org-scoped state (`importedMissions`,
     * `organizationDevices`, `deviceGroupIDs`, `appMode`, `notifications`,
     * `aeroToken`, `lastLoginTime`, style URL) is intentionally preserved.
     *
     * Called from `MapLayout.selectedDevice` watcher and `onUnmounted`.
     */
    $resetDeviceScopedState(): void {
      // Telemetry chain (re-populated by MQTT once the new device publishes).
      this.telemetry = null
      this.jobTelemetry = null
      lastHighFreqAt = 0
      this.rcState = null
      this.dockState = null
      this.dockStatus = null
      this.weatherData = null
      this.deviceHealthMessages = {}

      // Derived values that will refresh from telemetry / loadConfig.
      this.droneAmsl = 0
      this.geoidHeight = 0
      this.droneIncursions = null
      this.hasCriticalIncursions = false
      this.hasWarningIncursions = false
      this.currentFlightHeight = 0
      this.currentHeightSpeed = 0

      // Flight / device UI state.
      this.gtlPoints = []
      this.goToMusterPointActive = false
      this.automation_state = null
      this.isVirtualStickEnable = false
      this.recordingStatus = false
      this.activeImportedMission = null
      this.device = null

      // Timer leak defense: if a virtual-stick loop is running against the old
      // device, clear it before the handle is overwritten/lost.
      if (this.virtualStickInterval) {
        clearInterval(this.virtualStickInterval as ReturnType<typeof setInterval>)
        this.virtualStickInterval = null
      }
    },
    /**
     *
     * @param type 'droneConfig' | 'slinkPortalConfig' | 'deviceConfig' | 'failsafeConfig' | 'dockConfig'
     * @param deviceId String
     * @param opts Optional { signal } for cooperative cancellation on rapid device-group swaps.
     * @returns
     */
    async loadConfig(
      type: 'droneConfig' | 'slinkPortalConfig' | 'deviceConfig' | 'failsafeConfig' | 'dockConfig',
      deviceId: string,
      opts: { signal?: AbortSignal } = {},
    ) {
      try {
        const { organisationId } = useUserStore()
        const api = useApi()
        const url = `${import.meta.env.VITE_APP_HTTP_ENPOINT}device-management/${type}`
        const response = await api.get(url, {
          params: {
            organisationID: organisationId,
            deviceID: deviceId,
          },
          signal: opts.signal,
        })
        if (response.status !== 200) {
          this.addNotifications({
            notification: `Failed to load ${type}`,
            type: 'error',
            id: uuid(10),
          })
          return
        }
        if (response.data.deviceID && response.data.organisationID) {
          const { deviceID, organisationID } = response.data
          this.device = { deviceID, organisationID }
        }

        // Helper to parse config if it's a JSON string
        const parseConfig = (config: any) => {
          if (typeof config === 'string') {
            try {
              return JSON.parse(config)
            } catch (e) {
              log.error('Error parsing config string:', e)
              return null
            }
          }
          return config
        }

        if (type === 'droneConfig')
          this.droneConfigurations = parseConfig(response.data.droneConfig)
        if (type === 'slinkPortalConfig')
          this.slinkTechConfigurations = parseConfig(response.data.slinkPortalConfig)
        if (type === 'deviceConfig')
          this.cameraConfig = parseConfig(response.data.deviceConfig?.cameraConfig) || null
        if (type === 'failsafeConfig')
          this.failSafeConfigurations = parseConfig(response.data.failsafeConfig)
        if (type === 'dockConfig') this.dockConfigurations = parseConfig(response.data.dockConfig)
      } catch (error: any) {
        // Axios surfaces AbortSignal cancellations as either CanceledError or
        // ERR_CANCELED; silently swallow — the caller aborted intentionally.
        if (
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED' ||
          (error instanceof DOMException && error.name === 'AbortError')
        ) {
          return
        }
        log.error('Error loading config:', error)
        this.addNotifications({
          notification: 'An error occurred loading config',
          type: 'error',
          id: uuid(10),
        })
      }
    },
    async loadAllStreamConfig(): Promise<void> {
      const { organisationId } = useUserStore()
      const api = useApi()
      const stream = streamStore()
      const { allStreams, addSlinkTechStream } = stream
      const streamHttpURL = `${import.meta.env.VITE_APP_HTTP_ENPOINT}device-management/streamSettings`

      const loadStreamForDevice = async (deviceId: string, streamIndex: number): Promise<void> => {
        if (!deviceId || deviceId === '0000') return

        try {
          const body = {
            organisationID: organisationId,
            deviceID: deviceId,
            uid: 0,
          }

          const response = await api.post(streamHttpURL, body)

          if (response.status !== 200) {
            log.warn(`Failed to load stream config for device ${deviceId}`)
            return
          }

          const { streamSettings } = response.data

          if (allStreams[streamIndex]) {
            allStreams[streamIndex].stream_channel = streamSettings.channel_name
            allStreams[streamIndex].stream_token = streamSettings.token
            allStreams[streamIndex].device_name = deviceId
          }
        } catch (error) {
          log.error(`Error loading stream config for device ${deviceId}:`, error)
        }
      }

      try {
        // Load all stream configs in parallel
        const streamPromises: Promise<void>[] = []

        // Load RC/UAV stream (index 1)
        if (this.selectedRC && this.selectedRC !== '0000') {
          streamPromises.push(loadStreamForDevice(this.selectedRC, 1))
        }

        // Load Dock stream (index 0)
        if (this.selectedDock && this.selectedDock !== '0000') {
          streamPromises.push(loadStreamForDevice(this.selectedDock, 0))
        }

        // Handle SlinkTech stream dynamically
        if (this.selectedPortal && this.selectedPortal !== '0000') {
          // Add SlinkTech stream if it doesn't exist and load its config
          const slinkTechIndex = addSlinkTechStream()
          streamPromises.push(loadStreamForDevice(this.selectedPortal, slinkTechIndex))
        }

        await Promise.all(streamPromises)

        this.device = {
          deviceID: this.selectedRC || this.selectedDock,
          organisationID: organisationId || '',
        }
      } catch (error) {
        log.error('Error loading all stream configuration:', error)
        this.addNotifications({
          notification: 'An error occurred loading stream configurations',
          type: 'error',
          id: uuid(10),
        })
      }
    },

    async sendConfig(payload: any, urlPath: string): Promise<void> {
      const { organisationId } = useUserStore()
      const api = useApi()
      const url = `${
        import.meta.env.VITE_APP_HTTP_ENPOINT
      }device-management/${urlPath}?organisationID=${organisationId}&deviceID=${this.selectedRC}`

      try {
        const { sendCommand } = mqttStore()
        const response = await api.post(url, payload)

        this.addNotifications({
          notification: response.data.message,
          type: 'success',
          id: uuid(10),
        })
        await sendCommand('CONFIG_UPDATE')
      } catch (error) {
        log.error('Error sending configuration:', error)
        this.addNotifications({
          notification: 'Failed to send configuration',
          type: 'error',
          id: uuid(10),
        })
      }
    },

    async sendConfigAlt(payload: any, urlPath: string): Promise<void> {
      const { organisationId } = useUserStore()
      const api = useApi()
      const url = `${
        import.meta.env.VITE_APP_HTTP_ENPOINT
      }device-management/${urlPath}?organisationID=${organisationId}&deviceID=${this.selectedRC}`

      try {
        const response = await api.post(url, payload)

        this.addNotifications({
          notification: response.data.message,
          type: 'success',
          id: uuid(10),
        })
      } catch (error) {
        log.error('Error sending configuration:', error)
        this.addNotifications({
          notification: 'Failed to send configuration',
          type: 'error',
          id: uuid(10),
        })
      }
    },

    getStateFromWarningLevel(warningLevel: string): HealthLogState {
      switch (warningLevel) {
        case 'NORMAL':
        case 'NOTICE':
          return 'warning'
        case 'CAUTION':
        case 'WARNING':
          return 'error'
        case 'SERIOUS_WARNING':
        case 'UNKNOWN':
          return 'danger'
        default:
          return 'warning'
      }
    },

    reset(): void {
      // Using the properly defined getDefaultState function
      Object.assign(this, getDefaultState())
    },

    /**
     * Drops health messages older than `maxAgeMs` (FLY3D-1055). Idempotent and
     * only reassigns the record when something was actually removed, so the
     * one-second prune tick causes no reactive churn while nothing is stale.
     */
    pruneStaleHealthMessages(
      maxAgeMs: number = HEALTH_MESSAGE_TTL_MS,
      now: number = Date.now(),
    ): void {
      const next = pruneHealthMessages(this.deviceHealthMessages, now, maxAgeMs)
      if (countHealthMessages(next) !== countHealthMessages(this.deviceHealthMessages)) {
        this.deviceHealthMessages = next
      }
    },

    handleDeviceHealth(response: any): void {
      const newLog: DeviceHealthMessage = {
        info: response.description,
        code: response.informationCode,
        state: this.getStateFromWarningLevel(response.warningLevel),
        timeStamp: response.telDateTime,
        receivedAt: Date.now(),
      }

      if (!this.deviceHealthMessages[response.Module]) {
        this.deviceHealthMessages[response.Module] = []
      }
      this.deviceHealthMessages[response.Module].unshift(newLog)
      if (this.deviceHealthMessages[response.Module].length > MAX_HEALTH_MESSAGES_PER_MODULE) {
        this.deviceHealthMessages[response.Module].pop()
      }
    },

    getTooltipPosition(e: any): Record<string, number | undefined> {
      const { clientX, clientY } = e
      const { offsetTop, offsetHeight, offsetLeft, offsetWidth } = e.target
      const { innerWidth, innerHeight } = window
      const tooltipWidth = 150
      const tooltipHeight = 50
      let top: number | undefined
      let right: number | undefined
      let bottom: number | undefined
      let left: number | undefined

      if (innerWidth - clientX < offsetWidth + tooltipWidth) {
        right = offsetWidth + 2
      } else {
        left = offsetWidth + 2
      }

      if (innerHeight - clientY < offsetHeight + tooltipHeight) {
        bottom = offsetHeight + 2
      } else {
        top = offsetHeight + 2
      }

      const position = {
        top,
        right,
        bottom,
        left,
      }
      return position
    },

    showTooltip(e: any, content: any): void {
      this.tooltipPosition = this.getTooltipPosition(e)
      this.toolTipContent = content

      // Clear any existing timer
      if (this.toolTipTimer !== null) {
        clearTimeout(this.toolTipTimer)
      }
    },

    hideTooltip(): void {
      if (this.toolTipTimer !== null) {
        clearTimeout(this.toolTipTimer)
      }
      this.tooltipPosition = {}
      this.toolTipContent = null
    },

    /**
     * Merges low frequency telemetry into the main telemetry object while
     * preserving leaves owned by the high-frequency stream. Without this
     * preservation, each low-freq tick would briefly zero out gimbal pose,
     * heading, speed, etc. until the next high-freq tick arrived.
     */
    mergeLowFrequencyTelemetry(lowFreqData: Telemetry): void {
      if (!this.telemetry) {
        this.telemetry = lowFreqData
        return
      }

      try {
        const highFreqStale =
          lastHighFreqAt === 0 || Date.now() - lastHighFreqAt > HIGH_FREQ_STALE_MS
        const ownedPaths = highFreqStale
          ? new Set<string>()
          : new Set<string>(HIGH_FREQ_OWNED_PATHS)
        const walk = (source: any, target: any, path: string): void => {
          for (const key of Object.keys(source)) {
            const nextPath = path ? `${path}.${key}` : key
            if (ownedPaths.has(nextPath)) continue
            const value = source[key]
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
              if (
                target[key] === null ||
                typeof target[key] !== 'object' ||
                Array.isArray(target[key])
              ) {
                target[key] = {}
              }
              walk(value, target[key], nextPath)
            } else {
              target[key] = value
            }
          }
        }
        walk(lowFreqData, this.telemetry, '')
      } catch (error) {
        log.error('Error merging low frequency telemetry:', error)
      }
    },

    /**
     * Merges high frequency telemetry data into the main telemetry object
     * High frequency data takes priority over low frequency for fast-changing values
     */
    mergeHighFrequencyTelemetry(highFreqData: HighFrequencyTelemetry): void {
      lastHighFreqAt = Date.now()

      if (!this.telemetry) {
        // If no low frequency telemetry exists yet, skip
        return
      }

      try {
        // Update aircraft state with high frequency data
        if (this.telemetry.aircraftState) {
          this.telemetry.aircraftState.height = highFreqData.height
          this.telemetry.aircraftState.heading = highFreqData.attitude_head
          this.telemetry.aircraftState.speedX = highFreqData.speed_x
          this.telemetry.aircraftState.speedY = highFreqData.speed_y
          this.telemetry.aircraftState.speed = Math.sqrt(
            highFreqData.speed_x ** 2 + highFreqData.speed_y ** 2,
          )
          this.telemetry.aircraftState.distanceToHome = highFreqData.home_distance
        }

        // Update location navigation with high frequency data
        if (this.telemetry.locationNavigation) {
          this.telemetry.locationNavigation.latitude = highFreqData.latitude
          this.telemetry.locationNavigation.longitude = highFreqData.longitude
          this.telemetry.locationNavigation.ultrasonicHeight = highFreqData.ultrasonic_height
        }

        // Update gimbal camera with high frequency data
        if (this.telemetry.gimbalCamera?.gimbal) {
          this.telemetry.gimbalCamera.gimbal.pitch = highFreqData.gimbal_pitch
          this.telemetry.gimbalCamera.gimbal.roll = highFreqData.gimbal_roll
          this.telemetry.gimbalCamera.gimbal.yaw = highFreqData.gimbal_yaw
        }

        // Wind is deliberately left to the low-frequency `<RC>/TELEMETRY` merge.
        //
        // Both streams carry it, but in different units: the aggregated topic
        // divides DJI's 0.1 m/s into true m/s, while `TELEMTRY_HIGH_FREQ` relays
        // the DRC payload raw. Taking the high-frequency value here overwrote the
        // correct one for as long as the stream stayed fresh, so wind read 10x
        // high in flight and only snapped back to the truth once the aircraft
        // landed and the stream went stale — the "usually wrong, occasionally
        // goes green" report in FLY3D-732. The raw field also appears to lack the
        // 1-8 octant direction the aggregated topic carries, which is why the
        // direction showed "Unknown" alongside a live speed.
        //
        // Wind gains nothing from a 10 Hz update anyway. Do not reinstate this
        // without confirming the DRC payload's units against a real flight.
        // FLY3D-945, FLY3D-732, FLY3D-881
      } catch (error) {
        log.error('Error merging high frequency telemetry:', error)
      }
    },
  },
})
